import { FC, useMemo } from "react";
import clsx from "clsx";

import RoundedInfo from "icons/RoundedInfo";

import {
  StyledInputPhoneContainer,
  StyledInputPhone,
  StyledInputPhoneError,
  StyledInputPhoneLabel,
  StyledInputPhoneField,
  StyledInputPhoneErrorIcon,
} from "styles/components/inputs/InputPhone";

interface InputPhoneProps {
  disabled?: boolean;
  className?: string;
  required?: boolean;
  value?: string;
  label?: string;
  placeholder?: string;
  defaultCountry?: string;
  error?: boolean;
  errorText?: string;
  dataTestId?: string;
  withErrorField?: boolean;

  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
}

const InputPhone: FC<InputPhoneProps> = ({
  className,
  value,
  disabled,
  required,
  label = "Label",
  error,
  errorText = "Error text",
  placeholder = "Placeholder",
  defaultCountry = "il",
  dataTestId,
  withErrorField = true,
  onChange,
  onBlur,
  onFocus,
}) => {
  const wrapClass = clsx(["phone-input-container", className]);

  const inputClass = clsx("phone-input__input", {
    "phone-input__input_error": error,
    "phone-input__input_disabled": disabled,
  });

  const btnClassName = clsx("phone-input__btn", {
    "phone-input__btn_disabled": disabled,
  });

  const errorClassName = clsx("phone-input-error", {
    "phone-input-error_visible": error,
  });

  const renderedLabel = useMemo(
    () =>
      label ? (
        <StyledInputPhoneLabel
          variant="bodyM"
          data-testid={dataTestId && `${dataTestId}-label`}
        >
          {label}
          {required ? <span className="phone-input__marker">*</span> : null}
        </StyledInputPhoneLabel>
      ) : null,
    [dataTestId, label, required]
  );

  const renderedErrorIcon = useMemo(
    () =>
      error ? (
        <StyledInputPhoneErrorIcon
          data-testid={dataTestId && `${dataTestId}-error-icon`}
        >
          <RoundedInfo filled={false} />
        </StyledInputPhoneErrorIcon>
      ) : null,
    [dataTestId, error]
  );

  const renderedErrorField = useMemo(
    () =>
      withErrorField ? (
        <StyledInputPhoneError
          variant="bodyM"
          fontWeight={300}
          className={errorClassName}
          data-testid={dataTestId && `${dataTestId}-error`}
        >
          {errorText}
        </StyledInputPhoneError>
      ) : null,
    [dataTestId, errorClassName, errorText, withErrorField]
  );

  return (
    <StyledInputPhoneContainer className={wrapClass} data-testid={dataTestId}>
      <StyledInputPhoneField>
        {renderedLabel}
        <StyledInputPhoneContainer>
          <StyledInputPhone
            containerClass="phone-input"
            inputClass={inputClass}
            buttonClass={btnClassName}
            dropdownClass="phone-input__list"
            country={defaultCountry}
            specialLabel=""
            autocompleteSearch
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            onBlur={onBlur}
            onFocus={onFocus}
          />
          {renderedErrorIcon}
        </StyledInputPhoneContainer>
      </StyledInputPhoneField>
      {renderedErrorField}
    </StyledInputPhoneContainer>
  );
};

export default InputPhone;
